import { useEffect } from 'react'

// Integrate a modal / overlay with browser back so Android's system back
// button dismisses the modal instead of popping the SPA history stack.
// Without this, users hit the trap Jacob reported: multiple back presses
// to escape a scorecard because no modal/overlay had history integration.
//
// Usage:
//   const [open, setOpen] = useState(false)
//   useHistoryModal(open, () => setOpen(false), 'gps')
//
// `name` is stamped on history.state.tfModal so nested modals don't fight
// (each pushes + pops its own entry).

// Decide whether a popstate should CLOSE this overlay. `currentTopMarker` is
// history.state?.tfModal AFTER the pop — i.e. the entry now on top.
//   * currentTopMarker === name → OUR marker is still the top entry, so the pop
//     removed an entry BELOW us (a parent/sibling). We must NOT close, otherwise
//     a nested child closing (e.g. Change-tee inside Edit Round) cascades a
//     popstate that the still-mounted parent listener catches and closes the
//     parent too, dumping the user all the way out (defect 5).
//   * currentTopMarker !== name → OUR marker is gone from the top → WE were the
//     entry that got popped → close.
export function popClosesModal(currentTopMarker, name) {
  return currentTopMarker !== name
}

export default function useHistoryModal(open, onClose, name = 'modal') {
  useEffect(() => {
    if (!open) return
    // Push a marker entry so the next back press pops US, not the app.
    try {
      window.history.pushState({ tfModal: name }, '', window.location.href)
    } catch { /* private mode / cross-origin — silently degrade to old behaviour */ }

    const onPop = () => {
      // Only close when this pop actually removed OUR marker. A nested child
      // closing re-pops its own entry, surfacing a popstate whose new top is
      // THIS (parent) marker — popClosesModal returns false so we stay open.
      if (popClosesModal(window.history.state?.tfModal, name)) {
        onClose?.()
      }
    }
    window.addEventListener('popstate', onPop)

    return () => {
      window.removeEventListener('popstate', onPop)
      // If the modal closed via user tap (not back), our marker entry
      // is still on top. Consume it so the next back press behaves
      // normally instead of doing nothing. The popstate this emits lands
      // on any still-mounted parent overlay, whose onPop now sees its OWN
      // marker back on top and (via popClosesModal) ignores it — so
      // closing a nested child pops exactly one level.
      try {
        if (window.history.state?.tfModal === name) {
          window.history.back()
        }
      } catch { /* fine */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])
}
